"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import { FooterContent, SocialLink } from "../types";
import { SocialLinks, Copyright, getSocialIcon } from "../BaseFooter";
import { colors, footerStyles, layout, spacing } from "../tokens";

// ============================================================
// MINIMAL FOOTER - Stripped-down simple footer
// ============================================================
// Best for: Portfolio, Personal sites, Minimalist designs
// Features: Just the essentials, clean aesthetic
// ============================================================

interface MinimalFooterProps extends SectionContentProps {
  content: FooterContent;
}

export const MinimalFooter: React.FC<MinimalFooterProps> = ({
  content: propContent,
  onContentChange,
  isSelected,
}) => {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Dynamic colors with fallbacks
  const backgroundColor = content.backgroundColor || "#f8fafc"; // slate-50
  const textColor = content.textColor || "#0f172a"; // slate-900
  const accentColor = content.accentColor || "#3b82f6"; // blue-500

  const socialLinks: SocialLink[] = content.socialLinks || [
    { platform: "twitter", url: "#" },
    { platform: "github", url: "#" },
    { platform: "linkedin", url: "#" },
  ];

  const defaultLinks = ["Privacy", "Terms", "Contact"];
  const links = content.footerLinks || defaultLinks;

  return (
    <footer
      className={`border-t ${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        fontFamily,
        backgroundColor,
        color: textColor,
        borderColor: content.backgroundColor ? 'rgba(0,0,0,0.1)' : undefined
      }}
      role="contentinfo"
      aria-label="Minimal footer"
    >
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo / Brand */}
          <div className="flex items-center gap-2">
            {content.logo && (
              <img
                src={content.logo}
                alt="Logo"
                className="h-6 w-auto"
              />
            )}
            {content.companyName && (
              <span className="font-semibold text-slate-700">
                {content.companyName}
              </span>
            )}
          </div>

          {/* Navigation Links */}
          <nav aria-label="Footer navigation">
            <ul className="flex items-center gap-6 text-sm">
              {links.map((link, index) => (
                <li key={index}>
                  <a
                    href="#"
                    className="text-slate-500 hover:text-slate-900 transition-colors"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Social Links */}
          <SocialLinks links={socialLinks} size="sm" />
        </div>

        {/* Copyright */}
        <div className="mt-6 pt-6 border-t border-slate-200 text-center">
          <Copyright
            text={content.copyright || "© 2026 All rights reserved."}
            className="text-slate-400"
          />
        </div>
      </div>
    </footer>
  );
};

export default MinimalFooter;
